import React, { lazy, Suspense } from 'react'
import * as ReactDOM from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams, createBrowserRouter, RouterProvider } from 'react-router-dom'

import uiLocales from './components/shared/uiLocales'

import * as Sentry from '@sentry/react'

import swiperCss from 'swiper/css'
import swiperNavigationCss from 'swiper/css/navigation'
import swiperPaginationCss from 'swiper/css/pagination'
import swiperScrollbarCss from 'swiper/css/scrollbar'
import swiperEffectFadeCss from 'swiper/css/effect-fade'
import swiperEffectCubeCss from 'swiper/css/effect-cube'
import swiperEffectCoverflowCss from 'swiper/css/effect-coverflow'
import swiperEffectFlipCss from 'swiper/css/effect-flip'
import swiperEffectCardsCss from 'swiper/css/effect-cards'

const environment = process.env.NODE_ENV || 'development'
const defaultEnvSettings = { serverUrl: '', sentryDsnKey: '', helpCenterUrl: '' }

function initializeMonitoring(envSettings) {
  if (environment !== 'development' && envSettings.sentryDsnKey) {
    Sentry.init({
      dsn: envSettings.sentryDsnKey,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration()
      ],
      // Tracing
      tracesSampleRate: 0.05, // Capture 100% of the transactions
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [],
      // Session Replay
      replaysSessionSampleRate: 0.05, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    })
  }
}

async function loadEnvSettings() {
  try {
    const response = await fetch('/env_settings.json', { cache: 'no-store' })

    if (!response.ok) {
      throw new Error(`Failed to load env settings: ${ response.status }`)
    }

    return { ...defaultEnvSettings, ...(await response.json()) }

  } catch (error) {
    console.error(error)
    return defaultEnvSettings
  }
}

window.Color = '#FF5A1F'

const Settings = lazy(() => import('./components/admin/Settings'))
const BlockLibrary = lazy(() => import('./components/admin/block_library/BlockLibrary'))
const BlockLibraryItem = lazy(() => import('./components/admin/block_library/BlockLibraryItem'))

const MainBuilder = lazy(() => import('./components/builder/Main'))
const PreviewBuilder = lazy(() => import('./components/builder/Preview'))
const Migration = lazy(() => import('./components/migration/Migration'))

const DirectorAppWrapper = lazy(() => import('./components/director/shared/AppWrapper'))
const DirectorHome = lazy(() => import('./components/director/Home'))
const DirectorSignIn = lazy(() => import('./components/director/SignIn'))
const DirectorThemes = lazy(() => import('./components/director/themes/Themes'))
const DirectorTheme = lazy(() => import('./components/director/themes/Theme'))

const Error404 = lazy(() => import('./components/error_pages/Error404'))

const LocaleLayout = () => {
  const { locale } = useParams()

  if (uiLocales.includes(locale)) {
    return <Outlet/>

  } else {
    let redirectUrl = ''
    if (window.location.pathname.includes(`/${ locale }/`)) {
      redirectUrl = window.location.pathname.replace(`/${ locale }/`, '/en/') + window.location.search

    } else if (window.location.pathname.includes(`/${ locale }`)) {
      redirectUrl = window.location.pathname.replace(`/${ locale }`, '/en') + window.location.search
    }

    return <Navigate to={ redirectUrl }/>
  }
}

const getDirectorRoutes = envSettings => (
  <>
    <Route
      index
      element={
        <DirectorAppWrapper
          envSettings={ envSettings }
          Content={ DirectorHome }
        />
      }
    />

    <Route
      path='sign_in'
      element={
        <DirectorAppWrapper
          envSettings={ envSettings }
          Content={ DirectorSignIn }
        />
      }
    />

    <Route path='themes'>
      <Route
        index
        element={
          <DirectorAppWrapper
            envSettings={ envSettings }
            Content={ DirectorThemes }
          />
        }
      />

      <Route
        path=':themeUid'
        element={
          <DirectorTheme envSettings={ envSettings }/>
        }
      />
    </Route>
  </>
)

async function bootstrap() {
  const envSettings = await loadEnvSettings()
  const directorRoutes = getDirectorRoutes(envSettings)

  initializeMonitoring(envSettings)

  ReactDOM.createRoot(document.getElementById('app')).render(
    <BrowserRouter>
      <Suspense fallback={ <div/> }>
        <Routes>
          <Route
            path='contents/:contentUid'
            element={ <MainBuilder envSettings={ envSettings }/> }
          />

          <Route
            path='contents/preview'
            element={ <PreviewBuilder envSettings={ envSettings }/> }
          />

          <Route element={ <LocaleLayout/> }>
            <Route
              path=':locale/contents/:contentUid'
              element={ <MainBuilder envSettings={ envSettings }/> }
            />

            <Route
              path=':locale/contents/preview'
              element={ <PreviewBuilder envSettings={ envSettings }/> }
            />
          </Route>

          <Route
            path='migration'
            element={ <Migration envSettings={ envSettings }/> }
          />

          <Route element={ <LocaleLayout/> }>
            <Route
              path=':locale/migration'
              element={ <Migration envSettings={ envSettings }/> }
            />
          </Route>

          <Route
            path='settings'
            element={ <Settings envSettings={ envSettings }/> }
          />

          <Route element={ <LocaleLayout/> }>
            <Route
              path=':locale/settings'
              element={ <Settings envSettings={ envSettings }/> }
            />
          </Route>

          <Route
            path='block_library'
            element={ <BlockLibrary envSettings={ envSettings }/> }
          />

          <Route element={ <LocaleLayout/> }>
            <Route
              path=':locale/block_library'
              element={ <BlockLibrary envSettings={ envSettings }/> }
            />
          </Route>

          <Route
            path='block_library/:uid'
            element={ <BlockLibraryItem envSettings={ envSettings }/> }
          />

          <Route element={ <LocaleLayout/> }>
            <Route
              path=':locale/block_library/:uid'
              element={ <BlockLibraryItem envSettings={ envSettings }/> }
            />
          </Route>

          <Route path='letmein'>
            { directorRoutes }

            <Route
              path=':locale'
              element={ <LocaleLayout/> }
            >
              { directorRoutes }
            </Route>
          </Route>

          <Route
            path='page_not_found'
            element={ <Error404/> }
          />

          <Route
            path='*'
            element={ <Navigate to='/page_not_found'/> }
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}

bootstrap()
